import { cn } from '#client/lib/cn';
import { markerIsStroke, markerPath, seriesColor, seriesDash, seriesMarker } from './series';
import type { Series } from './types';

interface LegendProps {
  series: readonly Series[];
  /** Dims every entry but this one, following the cursor. */
  activeId?: string | undefined;
  className?: string | undefined;
}

/**
 * Which line is which.
 *
 * A real `<ul>`, because it is a list — and because a screen reader announcing
 * "list, four items" before reading them is genuinely the right summary.
 *
 * Each swatch carries the series' **marker and dash**, not just its colour.
 * That is what makes the legend usable by a reader who cannot separate the
 * hues, and it is the same redundancy the lines themselves use, so the two
 * match rather than merely coexisting.
 */
export function Legend({ series, activeId, className }: LegendProps) {
  if (series.length < 2) return null;

  return (
    <ul className={cn('c-chart-legend', className)}>
      {series.map((one, index) => {
        const colour = seriesColor(index, one.tone);
        const marker = seriesMarker(index);
        const stroked = markerIsStroke(marker);

        return (
          <li
            key={one.id}
            className={cn('c-chart-legend__item', activeId && activeId !== one.id && 'is-dimmed')}
          >
            {/* Decorative: the label beside it already says which series this
                is, and a second announcement of the same fact is noise. */}
            <svg className="c-chart-legend__swatch" viewBox="-8 -6 16 12" aria-hidden="true">
              <line
                x1={-7}
                y1={0}
                x2={7}
                y2={0}
                stroke={colour}
                strokeWidth={2}
                strokeDasharray={seriesDash(index)}
                strokeLinecap="round"
              />
              <path
                d={markerPath(marker, 6)}
                fill={stroked ? 'none' : colour}
                stroke={colour}
                strokeWidth={stroked ? 1.5 : 0}
              />
            </svg>
            <span>{one.label}</span>
          </li>
        );
      })}
    </ul>
  );
}
